import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import { FeatureInfoPanelData, FeatureInfoPanelRow } from './visor.types';

interface GuardTakeResponse {
  id: number;
  name?: string | null;
  municipality?: string | null;
  status?: string | null;
  statusDate?: string | null;
}

export type GuardTakeStatus = 'abierta' | 'cerrada';

export interface GuardTake {
  id: string;
  backendId: number;
  label: string;
  status: GuardTakeStatus;
  statusDate?: string;
  panelData: FeatureInfoPanelData;
}

export type GuardTakesResult =
  | { kind: 'ready'; takes: GuardTake[] }
  | { kind: 'unauthorized' }
  | { kind: 'unavailable' };

export type GuardTakeStatusUpdateResult =
  | { kind: 'ready'; take: GuardTake }
  | { kind: 'unauthorized' }
  | { kind: 'invalid'; message: string }
  | { kind: 'unavailable' };

@Injectable({ providedIn: 'root' })
export class GuardTakesService {
  async getCurrentTakes(headers: Record<string, string>): Promise<GuardTakesResult> {
    try {
      const response = await fetch(`${environment.apiWmsUrl}/guards/takes`, { headers });

      if (response.status === 401 || response.status === 403) {
        return { kind: 'unauthorized' };
      }

      if (!response.ok) {
        return { kind: 'unavailable' };
      }

      const rawTakes = await response.json() as GuardTakeResponse[];

      return {
        kind: 'ready',
        takes: rawTakes.map((take, index) => this.createTake(take, index))
      };
    } catch {
      return { kind: 'unavailable' };
    }
  }

  async updateStatus(
    take: GuardTake,
    status: GuardTakeStatus,
    headers: Record<string, string>
  ): Promise<GuardTakeStatusUpdateResult> {
    try {
      if (status === take.status) {
        return {
          kind: 'invalid',
          message: `La toma ya está ${status}.`
        };
      }

      const response = await fetch(`${environment.apiWmsUrl}/guards/takes/${take.backendId}/status`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      if (response.status === 401 || response.status === 403) {
        return { kind: 'unauthorized' };
      }

      if (response.status === 400 || response.status === 404) {
        const payload = await response.json().catch(() => undefined) as { error?: string } | undefined;
        return {
          kind: 'invalid',
          message: payload?.error || 'No se ha podido registrar la operación para esta toma.'
        };
      }

      if (!response.ok) {
        return { kind: 'unavailable' };
      }

      return {
        kind: 'ready',
        take: this.createTake(await response.json() as GuardTakeResponse)
      };
    } catch {
      return { kind: 'unavailable' };
    }
  }

  private createTake(take: GuardTakeResponse, index = 0): GuardTake {
    const backendId = take.id || index + 1;
    const label = this.getTrimmedText(take.name) || `Toma nº${backendId}`;
    const status = this.getKnownStatus(take.status);

    return {
      id: `${backendId}`,
      backendId,
      label,
      status,
      statusDate: this.getTrimmedText(take.statusDate) || undefined,
      panelData: this.createPanelData(take, label)
    };
  }

  private createPanelData(take: GuardTakeResponse, label: string): FeatureInfoPanelData {
    const rows: FeatureInfoPanelRow[] = [];
    const municipality = this.getTrimmedText(take.municipality);
    const status = this.getIrrigationStatus(take.status);

    if (municipality) {
      rows.push({
        label: 'Municipio',
        value: municipality,
        kind: 'take'
      });
    }

    rows.push({
      label: 'Estado de la toma',
      value: status.label,
      kind: 'irrigation-status',
      badge: status.badge
    });

    const formattedStatusDate = this.formatStatusDate(take.statusDate);
    if (formattedStatusDate) {
      rows.push({
        label: 'Última operación',
        value: formattedStatusDate,
        kind: 'irrigation-status'
      });
    }

    return {
      title: 'Mi toma',
      referenceLabel: 'Toma',
      cadastralReference: label,
      rows,
      profile: 'public'
    };
  }

  private getKnownStatus(value: string | null | undefined): GuardTakeStatus {
    const normalizedStatus = this.getNormalizedStatus(value);

    if (['abierto', 'abierta', 'open'].includes(normalizedStatus)) {
      return 'abierta';
    }

    return 'cerrada';
  }

  private getIrrigationStatus(value: string | null | undefined): { label: string; badge: 'open' | 'closed' } {
    if (this.getKnownStatus(value) === 'abierta') {
      return { label: 'Abierta', badge: 'open' };
    }

    return { label: 'Cerrada', badge: 'closed' };
  }

  private getNormalizedStatus(value: string | null | undefined): string {
    return this.getTrimmedText(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  private formatStatusDate(value: string | null | undefined): string {
    const rawDate = this.getTrimmedText(value);
    if (!rawDate) {
      return '';
    }

    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) {
      return rawDate;
    }

    return new Intl.DateTimeFormat('es-ES', {
      dateStyle: 'short',
      timeStyle: 'short'
    }).format(date);
  }

  private getTrimmedText(value: string | null | undefined): string {
    return value?.trim() ?? '';
  }
}
